import { getItems, on } from "./store.js";

/**
 * Pick the primary image for a record.
 * Prefers web-friendly variants, then falls back to master.
 * (Still used for the unfiltered walls; results list does not use images.)
 * @param {{src:string,type?:string,alt?:string}[]=} images
 * @returns {{src:string,type?:string,alt?:string}|null}
 */
function pickMainImage(images = []) {
  if (!images.length) return null;
  const pref = ["image_web", "image_thumb", "image_master", ""];
  return [...images].sort(
    (a, b) => pref.indexOf(a.type || "") - pref.indexOf(b.type || "")
  )[0];
}

/**
 * Determine if an item is a “music” item (strict: must have inline PAE).
 * @param {import("./store.js").Item} item
 * @returns {boolean}
 */
function isMusicItem(item) {
  return !!item?.incipit?.paeInline;
}

/**
 * Render a list of items as a grid of thumbs into a wall element.
 * Uses a <template id="thumb-tpl"> with an <a.thumb><img/></a> inside.
 * (For base walls only; results wall uses a custom renderer below.)
 * @param {HTMLElement|null} wallEl
 * @param {import("./store.js").Item[]} items
 * @param {HTMLTemplateElement|null} tpl
 * @param {{baseMediaPath?: string}=} opts
 */
function renderInto(
  wallEl,
  items,
  tpl,
  { baseMediaPath = "/data/images/" } = {}
) {
  if (!wallEl || !tpl) return;
  wallEl.innerHTML = "";

  for (const it of items) {
    const mainImg = pickMainImage(it.media?.images);
    if (!mainImg?.src) continue;

    const node = tpl.content.cloneNode(true);
    const a = /** @type {HTMLAnchorElement} */ (node.querySelector("a.thumb"));
    const img = /** @type {HTMLImageElement} */ (node.querySelector("img"));

    if (a) a.href = `/detail.html?id=${encodeURIComponent(it.id)}`;
    if (img) {
      img.src = baseMediaPath + mainImg.src; // keep prefix for detail.js compatibility
      img.alt = it.title || "Exhibit image";
    }

    wallEl.appendChild(node);
  }
}

/**
 * Attempt to derive a label for “kind” (e.g., manuscript/composition) from categories.
 * @param {import("./store.js").Item} it
 * @returns {string}
 */
function extractKind(it) {
  const cats = it.categories || [];
  return cats[0] || "—";
}

/**
 * Convert a note name (C, F#, Bb, etc) into a pitch class (0–11).
 * Accepts German H → B.
 * @param {string} name
 * @returns {number|null}
 */
function nameToPC(name) {
  let n = String(name || "").trim();
  if (!n) return null;
  n = n.replace(/^H/i, "B");
  const m = n.match(/^([A-Ga-g])([#b]?)/);
  if (!m) return null;
  const letter = m[1].toUpperCase();
  const acc = m[2] || "";
  const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[letter];
  let pc = base;
  if (acc === "#") pc = (pc + 1) % 12;
  if (acc === "b") pc = (pc + 11) % 12;
  return pc;
}

/**
 * Parse a keyboard/typed pattern (e.g., "CFFG" or "C4F4F4G4") into pitch-classes.
 * Ignores octave numbers.
 * @param {string} str
 * @returns {number[]}
 */
function pcsFromPattern(str) {
  const tokens = (str.match(/[A-Ga-g](?:#|b)?\d?/g) || []).map((t) =>
    t.replace(/\d+$/, "")
  );
  return tokens.map(nameToPC).filter((v) => v !== null);
}

/**
 * Extract pitch-classes from a Plaine & Easie string.
 * Uses @data: block if present. Normalizes '-'→'b', '+'→'#', and H→B.
 * @param {string} pae
 * @returns {number[]}
 */
function pcsFromPAE(pae) {
  let data = (pae || "").trim();
  const m = data.match(/@data:([\s\S]+)/i);
  if (m) data = m[1];
  data = data.replace(/-/g, "b").replace(/\+/g, "#").replace(/\bH/gi, "B");
  const tokens = data.match(/[A-Ga-g](?:#|b|n)?/g) || [];
  const out = [];
  for (let t of tokens) {
    t = t.replace(/n$/i, "");
    const pc = nameToPC(t);
    if (pc !== null) out.push(pc);
  }
  return out;
}

/**
 * Create interval sequence (mod 12) from a pitch-class sequence.
 * @param {number[]} seq
 * @returns {number[]}
 */
function intervals(seq) {
  const iv = [];
  for (let i = 1; i < seq.length; i++) iv.push((seq[i] - seq[i - 1] + 12) % 12);
  return iv;
}

/**
 * Find the matching window (start index) of pattPCs in targetPCs.
 * - If transpose=false → exact PC matching.
 * - If transpose=true → interval matching (transpose-insensitive).
 * - If startOnly=true → only match at index 0.
 * @param {number[]} targetPCs
 * @param {number[]} pattPCs
 * @param {{startOnly:boolean, transpose:boolean}} opts
 * @returns {{start:number, len:number}|null}
 */
function findMatchWindow(targetPCs, pattPCs, { startOnly, transpose }) {
  const n = pattPCs.length;
  if (!n) return null;
  if (!targetPCs.length || targetPCs.length < n) return null;

  if (!transpose) {
    const hi = startOnly ? 0 : targetPCs.length - n;
    for (let i = 0; i <= hi; i++) {
      let ok = true;
      for (let j = 0; j < n; j++) {
        if (targetPCs[i + j] !== pattPCs[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return { start: i, len: n };
      if (startOnly) break;
    }
    return null;
  }

  // interval-based
  if (n === 1) {
    if (startOnly)
      return targetPCs[0] === pattPCs[0] ? { start: 0, len: 1 } : null;
    const idx = targetPCs.indexOf(pattPCs[0]);
    return idx >= 0 ? { start: idx, len: 1 } : null;
  }

  const patIv = intervals(pattPCs);
  const tgtIv = intervals(targetPCs);
  const m = patIv.length;
  const hi = startOnly ? 0 : tgtIv.length - m;
  for (let i = 0; i <= hi; i++) {
    let ok = true;
    for (let j = 0; j < m; j++) {
      if (tgtIv[i + j] !== patIv[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return { start: i, len: n };
    if (startOnly) break;
  }
  return null;
}

/**
 * Render a single result item as a text block + Verovio incipit SVG.
 * Highlights the matched note window in blue (best effort on notehead elements).
 * @param {HTMLElement} parent
 * @param {import("./store.js").Item} it
 * @param {{pattern:string, position:"start"|"any", transpose:boolean}} f
 * @returns {Promise<void>}
 */
async function renderResultRecord(parent, it, f) {
  const wrap = document.createElement("article");
  wrap.className = "mb-6 pb-6 border-b border-gray-200";

  // Header block: composer / title / kind
  const composer = it.composers?.join(", ") || "—";
  const kind = extractKind(it);

  const hdr = document.createElement("div");
  hdr.innerHTML = `
    <div class="text-sm text-gray-600">${composer}</div>
    <div class="text-xl font-bold">${it.title || "—"}</div>
    <div class="text-xs uppercase tracking-wide text-gray-500">${kind}</div>
  `;
  wrap.appendChild(hdr);

  // Verovio SVG container
  const scoreBox = document.createElement("div");
  scoreBox.className = "mt-3";
  wrap.appendChild(scoreBox);

  // Render with Verovio (same UMD wasm you use on detail page)
  await waitForVerovio();
  const tk = new window.verovio.toolkit();
  tk.setOptions({
    scale: 40,
    pageHeight: 100,
    adjustPageHeight: true,
    footer: "none",
  });
  const svg = tk.renderData(it.incipit.paeInline, { inputFrom: "pae" });
  scoreBox.innerHTML = svg;

  // Highlight matched notes: find window in PCs and color that many noteheads
  try {
    const pattPCs = pcsFromPattern(f.pattern || "");
    const tgtPCs = pcsFromPAE(it.incipit.paeInline || "");
    const startOnly = f.position === "start";
    const win = findMatchWindow(tgtPCs, pattPCs, {
      startOnly,
      transpose: !!f.transpose,
    });

    if (win) {
      // Attempt to select rendered noteheads in logical order.
      // Verovio SVG typically contains note groups with <use> or <path> elements.
      // We take a pragmatic approach: select candidates and color a window.
      const svgRoot = scoreBox.querySelector("svg");
      if (svgRoot) {
        // Prefer notehead paths; fallback to generic 'note' groups
        let noteNodes = svgRoot.querySelectorAll(
          "g.note, g.layer g.chord, g.layer g.note, use.notehead, path.notehead"
        );
        if (!noteNodes || noteNodes.length === 0) {
          // very defensive fallback: any path inside note groups
          noteNodes = svgRoot.querySelectorAll(
            "g.note path, g.chord path, g.layer path"
          );
        }
        const arr = Array.from(noteNodes);
        const start = win.start;
        const end = start + win.len;

        for (let i = start; i < end && i < arr.length; i++) {
          const el = arr[i];
          // Color fill/stroke for visibility across render modes
          if (el instanceof SVGElement) {
            el.style.fill = "rgb(37, 99, 235)"; // tailwind blue-600
            el.style.stroke = "rgb(37, 99, 235)";
          }
        }
      }
    }
  } catch (e) {
    // Non-fatal: highlighting is best-effort; keep the SVG visible
    console.warn("Highlight failed:", e);
  }

  parent.appendChild(wrap);
}

/**
 * Wait for the Verovio UMD WASM build to be initialized on `window.verovio`.
 * Resolves once the runtime is ready.
 * @returns {Promise<void>}
 */
function waitForVerovio() {
  return new Promise((resolve) => {
    if (window.verovio?.module?.calledRun) return resolve();

    const mod = window.verovio?.module;
    if (!mod) {
      const t = setInterval(() => {
        if (window.verovio?.module) {
          clearInterval(t);
          waitForVerovio().then(resolve);
        }
      }, 10);
      return;
    }

    const prev = mod.onRuntimeInitialized;
    mod.onRuntimeInitialized = () => {
      if (typeof prev === "function") prev();
      resolve();
    };
  });
}

/**
 * Initialize the image walls:
 * - On data:ready → render two base walls (music / objects).
 * - On filter:changed → render textual result records (no thumbnails) into #filtered-results.
 */
(function initImageWall() {
  const wallObjects = document.getElementById("image-wall");
  const wallMusic = document.getElementById("music-wall");
  const resultsSection = document.getElementById("music-results"); // <section>
  const resultsList = document.getElementById("filtered-results"); // inner <div>
  const tpl = /** @type {HTMLTemplateElement|null} */ (
    document.getElementById("thumb-tpl")
  );

  on("data:ready", () => {
    const items = getItems();
    const music = items.filter(isMusicItem);
    const others = items.filter((it) => !isMusicItem(it));

    if (wallMusic) {
      if (music.length) renderInto(wallMusic, music, tpl);
      else
        wallMusic.innerHTML =
          '<p class="text-sm text-gray-500">Keine musikalischen Einträge.</p>';
    }
    if (wallObjects) {
      if (others.length) renderInto(wallObjects, others, tpl);
      else
        wallObjects.innerHTML =
          '<p class="text-sm text-gray-500">Keine Objekte gefunden.</p>';
    }

    if (resultsSection) resultsSection.classList.add("hidden");
    if (resultsList) resultsList.innerHTML = "";
  });

  on("filter:changed", async ({ filtered, filters }) => {
    if (!resultsSection || !resultsList) return;

    const patt = (filters?.incipitPattern || "").trim();
    if (!patt) {
      resultsSection.classList.add("hidden");
      resultsList.innerHTML = "";
      return;
    }

    resultsSection.classList.remove("hidden");
    resultsList.innerHTML = "";

    if (!filtered.length) {
      resultsList.innerHTML =
        '<p class="text-sm text-gray-500">Keine Treffer.</p>';
      return;
    }

    // Render textual records with Verovio per item
    for (const it of filtered) {
      await renderResultRecord(resultsList, it, {
        pattern: patt,
        position: filters.position === "any" ? "any" : "start",
        transpose: !!filters.transpose,
      });
    }
  });
})();
